'use client';

import { format } from 'date-fns';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { Link as TransitionLink } from 'next-view-transitions';
import React from 'react';
import Balancer from 'react-wrap-balancer';
import { BlurImage } from '@/components/blur-image';
import { strapiImage } from '@/lib/strapi/strapiImage';
import { truncate } from '@/lib/utils';
import { components } from '@/types/strapi';
import { Article } from '@/types/types';

interface BlogCardProps {
	article: Article;
	locale: string;
	priority?: boolean;
}

export function BlogCard({ article, priority = false }: BlogCardProps) {
	return (
		<motion.article
			initial={{ opacity: 0, y: 20 }}
			whileInView={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.4 }}
			className="blog-card group relative flex flex-col overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-sm transition-all duration-300 hover:border-neutral-300 hover:shadow-md">
			<noscript>
				<style>{`
					.blog-card[style*='opacity'],
					.blog-card [style*='opacity'],
					.blog-card [style*='translateY'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="relative aspect-[16/9] overflow-hidden bg-neutral-100">
				<TransitionLink href={`/blog/${article.slug}`}>
					{article.image && article.image.url && (
						<Image
							src={strapiImage(article.image.url)}
							alt={article.title}
							fill
							className="object-cover transition duration-300 group-hover:scale-105"
							priority={priority}
						/>
					)}
				</TransitionLink>
			</div>

			<div className="flex flex-grow flex-col p-6">
				<div className="mb-4 flex items-center gap-x-4 text-sm">
					<time
						dateTime={article.createdAt}
						className="text-neutral-500">
						{format(new Date(article.createdAt), 'MMMM dd, yyyy')}
					</time>
					{article.categories && article.categories.length > 0 && (
						<div className="flex flex-wrap gap-2">
							{article.categories.map((category, idx) => (
								<Link
									key={idx}
									href={`/blog?category=${category.slug}`}
									className="category-link rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
									{category.name}
								</Link>
							))}
						</div>
					)}
				</div>

				<TransitionLink
					href={`/blog/${article.slug}`}
					className="transition-opacity group-hover:opacity-75">
					<h2 className="mb-3 text-xl font-bold tracking-tight text-neutral-900 sm:text-2xl">
						{article.title}
					</h2>
				</TransitionLink>

				<p className="mb-4 line-clamp-2 text-sm text-neutral-600">
					{article.description}
				</p>

				<div className="mt-auto">
					<TransitionLink
						href={`/blog/${article.slug}`}
						className="inline-flex items-center text-sm font-medium text-primary transition-colors hover:text-primary/80">
						Read more
						<svg
							className="ml-1 h-4 w-4"
							viewBox="0 0 16 16"
							fill="none">
							<path
								d="M6.5 3.5l4 4.5-4 4.5"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</TransitionLink>
				</div>
			</div>
		</motion.article>
	);
}

export const BlogCardVertical = ({
	article,
	// locale,
}: {
	article: components['schemas']['Article'];
	locale: string;
}) => {
	return (
		<Link
			className="group w-full overflow-hidden rounded-3xl border border-transparent transition duration-200 hover:scale-[1.02] hover:border-neutral-200 hover:bg-white hover:shadow-md"
			href={`/blog/${article.slug}`}>
			<div className="">
				{article.image && (
					<BlurImage
						src={strapiImage(article.image?.url ?? '')}
						alt={article.title ?? ''}
						height="800"
						width="800"
						className="h-64 w-full rounded-3xl object-cover object-top md:h-96"
					/>
				)}
			</div>
			<div className="flex flex-col justify-between p-4 md:p-8">
				<div>
					<div className="mb-4 flex flex-wrap gap-4">
						{article.categories?.map((category, idx) => (
							<Link
								key={`category-${idx}`}
								href={`/blog?category=${category.slug}`}
								className="category-link rounded-full bg-neutral-100 px-4 py-2 text-xs font-bold capitalize text-neutral-700">
								{category.name}
							</Link>
						))}
					</div>
					<p className="mb-4 text-lg font-bold text-neutral-900 md:text-xl">
						<Balancer>{article.title}</Balancer>
					</p>
					<p className="mt-2 text-left text-sm text-neutral-600 md:text-base">
						{truncate(article.description || '', 500)}
					</p>
				</div>
				<div className="mt-6 flex items-center space-x-2">
					{/* <Image
							src={article.authorAvatar}
							alt={article.author}
							width={20}
							height={20}
							className="rounded-full h-5 w-5"
						/>
						<p className="text-sm font-normal text-muted">{article.author}</p> */}
					{/* <div className="h-1 w-1 bg-neutral-300 rounded-full"></div> */}
					<p className="max-w-xl text-sm text-neutral-500 transition duration-200">
						{format(
							new Date(article.publishedAt ?? ''),
							'MMMM dd, yyyy',
						)}
					</p>
				</div>
			</div>
		</Link>
	);
};
